import { ErrorHandler, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { BehaviorMapValidator, ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';
import { UserDetails } from '../models/user-details.model';
import { UserDataService } from './user-data.service';
import { Title } from "@angular/platform-browser";
import { behaviorMap } from '../models/behavior';

@Injectable({ providedIn: 'root' })
export class AppInsightsService {
    appInsights: ApplicationInsights;
    appInsightsConnectionString: string = '';
    userContext: UserDetails = {} as UserDetails;
    private angularPlugin = new AngularPlugin();
    private clickPluginInstance = new ClickAnalyticsPlugin();
    private isProduction = true;

    constructor(private _router: Router, private _userDataService: UserDataService, private _title: Title) {

        this.appInsightsConnectionString = this.getAppInsightsConnectionString();

        const clickPluginConfig = {
            dataTags:{
                customDataPrefix: 'data-custom-',
                parentDataTag: 'parent-group',
                useDefaultContentNameOrId: this.isProduction ? true : false,
            },
            autoCapture: this.isProduction ? true : false,
            behaviorValidator: BehaviorMapValidator(behaviorMap),
            defaultRightClickBhvr: 9
        };
        this.appInsights = new ApplicationInsights({
            config: {
                connectionString: this.appInsightsConnectionString,
                extensions: [this.angularPlugin, this.clickPluginInstance],
                extensionConfig: {
                    [this.angularPlugin.identifier]: { router: this._router, errorService: [new ErrorHandler()] },
                    [this.clickPluginInstance.identifier]: clickPluginConfig
                },
                enableAutoRouteTracking: this.isProduction ? true : false,
                autoTrackPageVisitTime: this.isProduction ? true : false
            }
        });
        this.appInsights.loadAppInsights();
        this.appInsights.trackPageView();
        this._userDataService.userDetails$.subscribe(user => {
            if (this.isProduction && user && user.roleId) {
                this.userContext = user;

                this.appInsights.setAuthenticatedUserContext(this.userContext.userId.toString(), this.userContext.email);
                this.appInsights.context.user.id = this.userContext.userId.toString();
                this.appInsights.context.user.accountId = this.userContext.email;
            }
        });
    }

    getAppInsightsConnectionString(): string {
        let appInsightsConnectionString = 'InstrumentationKey=' + environment.appInsights.instrumentationKey + ';' + 'IngestionEndpoint=' + environment.appInsights.ingestionEndPoint + ';' + 'LiveEndpoint=' + environment.appInsights.liveEndPoint;
        return appInsightsConnectionString;
    }

    logException(exception: Error, severityLevel?: number): void {
        if (this.isProduction) {
            this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
        }
    }

    logPageView(componentId: string, url: string = window.location.pathname): void {
        if (this.isProduction) {
            this.appInsights.trackPageView({ name: componentId, uri: url });
        }
    }

    logEvent(name: string, properties?: { [key: string]: any }) {
        properties["refUri"] = window.location.pathname;
        properties["pageName"] = this._title.getTitle();
        properties["parentId"] = this._title.getTitle();
        properties["actionType"] = "CL";
        if (this.isProduction) {
            this.appInsights.trackEvent({ name: name }, properties);
        }
    }
}
